import React from 'react';
import { Box, Select, MenuItem, FormControl } from '@mui/material';
import { CMButton } from './CMButton';
import { ApiEndpoint } from '../../types';

interface EndpointFilterProps {
  availableCategories: string[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  apiEndpoints: ApiEndpoint[];
  compact?: boolean;
}

const EndpointFilter: React.FC<EndpointFilterProps> = ({
  availableCategories,
  selectedCategory,
  onCategoryChange,
  apiEndpoints,
  compact = false,
}) => {
  console.log('🔍 [ENDPOINT-FILTER-DEBUG] EndpointFilter component render:', {
    availableCategoriesLength: availableCategories.length,
    availableCategories,
    selectedCategory,
    compact,
    apiEndpointsLength: apiEndpoints.length,
    willReturnNull: availableCategories.length === 0,
    timestamp: new Date().toISOString()
  });

  if (availableCategories.length === 0) {
    console.log('🔍 [ENDPOINT-FILTER-DEBUG] EndpointFilter returning null - no categories');
    return null;
  }

  // Create endpoint name mapping (same as RelatedItemsRing)
  const returnedRelationshipsKeyToName = Object.fromEntries(
    apiEndpoints.map(ep => [ep.returnedRelationshipsKey, ep.name])
  );

  console.log('🔍 [ENDPOINT-FILTER-DEBUG] EndpointFilter rendering decision:', {
    availableCategoriesLength: availableCategories.length,
    selectedCategory,
    renderingPath: availableCategories.length === 1 ? 'single-category' : 
                   availableCategories.length >= 2 ? 'dropdown' : 'no-categories',
    dropdownOptions: ["all"].concat(availableCategories),
    timestamp: new Date().toISOString()
  });

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      py: 1,
    }}>
      {availableCategories.length === 1 ? (
        // Single category - just show the name (same as RelatedItemsRing)
        (() => {
          console.log('🔍 [ENDPOINT-FILTER-DEBUG] Rendering single category button');
          return (
            <CMButton
              variant="outlined"
              sx={{
                pointerEvents: "all",
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
              }}
            >
              {returnedRelationshipsKeyToName[availableCategories[0]] || availableCategories[0].toUpperCase()}
            </CMButton>
          );
        })()
      ) : availableCategories.length >= 2 ? (
        // Multiple categories - show dropdown (same as RelatedItemsRing)
        (() => {
          console.log('🔍 [ENDPOINT-FILTER-DEBUG] Rendering dropdown with categories:', availableCategories);
          return (
            <CMButton
              variant="outlined"
              sx={{
                flexDirection: "column",
                pointerEvents: "all",
                color: 'rgba(255, 255, 255, 0.8)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
              }}
            >
              <Box sx={{display: "block", width: "100%", fontSize: '0.7rem', mb: 0.5}}>
                Displaying
              </Box>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedCategory}
                  onChange={(e) => {
                    const newValue = e.target.value as string;
                    console.log('🔍 [ENDPOINT-FILTER-DEBUG] Select onChange triggered:', {
                      previousValue: selectedCategory,
                      newValue,
                      availableCategories,
                      timestamp: new Date().toISOString()
                    });
                    onCategoryChange(newValue);
                  }}
                  sx={{
                    color: "white",
                    height: "24px",
                    fontSize: '0.8rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiSelect-icon': {
                      color: 'white',
                    },
                  }}
                >
                  {["all"].concat(availableCategories).map(category => {
                    console.log('🔍 [ENDPOINT-FILTER-DEBUG] Rendering MenuItem:', {
                      category,
                      displayName: category === 'all' ? 'All Sources' : 
                                 (returnedRelationshipsKeyToName[category] || category.toUpperCase())
                    });
                    return (
                      <MenuItem
                        key={category}
                        value={category}
                        sx={{
                          pointerEvents: "all",
                          fontSize: '0.8rem',
                        }}
                      >
                        {category === 'all' ? 'All Sources' : 
                         (returnedRelationshipsKeyToName[category] || category.toUpperCase())}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </CMButton>
          );
        })()
      ) : (
        // No categories
        (() => {
          console.log('🔍 [ENDPOINT-FILTER-DEBUG] Rendering no categories message');
          return (
            <CMButton
              variant="outlined"
              sx={{
                pointerEvents: "all",
                color: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(255, 255, 255, 0.2)',
              }}
            >
              No Related Items
            </CMButton>
          );
        })()
      )}
    </Box>
  );
};

export default EndpointFilter;
